import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProcessExecutionResponse } from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { ProcessExecutionPublicController } from "api/main/ProcessExecutionPublicController";
import { adminTheme, statusColors } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

export default function UserProcessExecutionList() {
  const api = useRef(createApi("processexecution") as ProcessExecutionPublicController);
  const navigate = useNavigate();
  const [executions, setExecutions] = useState<ProcessExecutionResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getData();
  }, []);

  async function getData() {
    setLoading(true);
    try {
      const data = await api.current.findAllByUser();
      // Sort by ID descending (newest first)
      const sorted = (data || []).sort(
        (a: ProcessExecutionResponse, b: ProcessExecutionResponse) => (b.id || 0) - (a.id || 0)
      );
      setExecutions(sorted);
    } catch (error) {
      console.error("Error fetching executions:", error);
    }
    setLoading(false);
  }

  const getProgressPercentage = (execution: ProcessExecutionResponse) => {
    const completed = execution.stepExecutions?.filter(s => s.status === "FINISHED").length || 0;
    const total = execution.stepExecutions?.length || 1;
    return Math.round((completed / total) * 100);
  };

  const getCurrentStepIndex = (execution: ProcessExecutionResponse) => {
    const activeIndex = execution.stepExecutions?.findIndex(s => s.status === "ACTIVE");
    if (activeIndex !== undefined && activeIndex >= 0) return activeIndex;
    // If no active step, return last finished or 0
    const lastFinished = execution.stepExecutions?.reduce((lastIdx, step, idx) =>
      step.status === "FINISHED" ? idx : lastIdx, -1);
    return lastFinished !== undefined && lastFinished >= 0 ? lastFinished : 0;
  };

  const handleContinue = (execution: ProcessExecutionResponse) => {
    const stepIndex = getCurrentStepIndex(execution);
    navigate(`/process/execution/${execution.id}/${stepIndex}`);
  };

  // Split executions into active and completed
  const activeExecutions = executions.filter(e => e.status !== "FINISHED");
  const completedExecutions = executions.filter(e => e.status === "FINISHED");

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: adminTheme.colors.textPrimary,
            }}
          >
            Mano procesai
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: adminTheme.colors.textSecondary }}
          >
            Peržiūrėkite ir tęskite pradėtus procesus
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats Cards */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Vykdomi procesai
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.primary }}
            >
              {activeExecutions.length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Užbaigti procesai
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.success }}
            >
              {completedExecutions.length}
            </Typography>
          </Card>
        </Box>

        {/* Active Processes Table */}
        {activeExecutions.length > 0 && (
          <>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, color: adminTheme.colors.textPrimary }}
            >
              Vykdomi procesai
            </Typography>
            <Card
              sx={{
                borderRadius: adminTheme.borderRadius.lg,
                border: `1px solid ${adminTheme.colors.border}`,
                boxShadow: "none",
                overflow: "hidden",
                mb: 4,
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: adminTheme.colors.backgroundAlt,
                        "& th": {
                          fontWeight: 600,
                          color: adminTheme.colors.textPrimary,
                          borderBottom: `2px solid ${adminTheme.colors.border}`,
                          py: 2,
                        },
                      }}
                    >
                      <TableCell>Proceso pavadinimas</TableCell>
                      <TableCell>Progresas</TableCell>
                      <TableCell>Būsena</TableCell>
                      <TableCell align="right">Veiksmas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeExecutions.map((execution) => (
                      <TableRow
                        key={execution.id}
                        sx={{
                          cursor: "pointer",
                          transition: adminTheme.transitions.fast,
                          "&:hover": {
                            bgcolor: "rgba(99, 102, 241, 0.04)",
                          },
                        }}
                        onClick={() => handleContinue(execution)}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: adminTheme.borderRadius.sm,
                                bgcolor: "rgba(99, 102, 241, 0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: adminTheme.colors.primary,
                              }}
                            >
                              <HourglassEmptyIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: adminTheme.colors.textPrimary,
                              }}
                            >
                              {execution.process?.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 150 }}>
                            <LinearProgress
                              variant="determinate"
                              value={getProgressPercentage(execution)}
                              sx={{
                                flex: 1,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: adminTheme.colors.borderLight,
                                "& .MuiLinearProgress-bar": {
                                  background: adminTheme.gradients.primary,
                                  borderRadius: 4,
                                },
                              }}
                            />
                            <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, minWidth: 40 }}>
                              {getProgressPercentage(execution)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Vykdomas"
                            size="small"
                            sx={{
                              bgcolor: statusColors.in_progress?.bg || "rgba(99, 102, 241, 0.1)",
                              color: statusColors.in_progress?.text || adminTheme.colors.primary,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<PlayArrowIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContinue(execution);
                            }}
                            sx={{
                              background: adminTheme.gradients.primary,
                              "&:hover": { background: adminTheme.gradients.primaryHover },
                            }}
                          >
                            Tęsti
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        {/* Completed Processes Table */}
        {completedExecutions.length > 0 && (
          <>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, color: adminTheme.colors.textPrimary }}
            >
              Užbaigti procesai
            </Typography>
            <Card
              sx={{
                borderRadius: adminTheme.borderRadius.lg,
                border: `1px solid ${adminTheme.colors.border}`,
                boxShadow: "none",
                overflow: "hidden",
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: adminTheme.colors.backgroundAlt,
                        "& th": {
                          fontWeight: 600,
                          color: adminTheme.colors.textPrimary,
                          borderBottom: `2px solid ${adminTheme.colors.border}`,
                          py: 2,
                        },
                      }}
                    >
                      <TableCell>Proceso pavadinimas</TableCell>
                      <TableCell>Būsena</TableCell>
                      <TableCell align="right">Veiksmas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {completedExecutions.map((execution) => (
                      <TableRow
                        key={execution.id}
                        sx={{
                          cursor: "pointer",
                          transition: adminTheme.transitions.fast,
                          "&:hover": {
                            bgcolor: "rgba(99, 102, 241, 0.04)",
                          },
                        }}
                        onClick={() => handleContinue(execution)}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: adminTheme.borderRadius.sm,
                                bgcolor: "rgba(16, 185, 129, 0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: adminTheme.colors.success,
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: adminTheme.colors.textPrimary,
                              }}
                            >
                              {execution.process?.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Užbaigtas"
                            size="small"
                            sx={{
                              bgcolor: statusColors.finished?.bg || "rgba(16, 185, 129, 0.1)",
                              color: statusColors.finished?.text || adminTheme.colors.success,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Peržiūrėti">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleContinue(execution);
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        {/* Empty state */}
        {executions.length === 0 && !loading && (
          <Card
            sx={{
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
              p: 6,
              textAlign: "center",
            }}
          >
            <AccountTreeIcon
              sx={{ fontSize: 48, mb: 2, opacity: 0.5, color: adminTheme.colors.textMuted }}
            />
            <Typography sx={{ color: adminTheme.colors.textMuted, mb: 2 }}>
              Dar nepradėjote jokių procesų
            </Typography>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() => navigate("/available-processes")}
              sx={{
                background: adminTheme.gradients.primary,
                "&:hover": { background: adminTheme.gradients.primaryHover },
              }}
            >
              Peržiūrėti galimus procesus
            </Button>
          </Card>
        )}
      </Container>
    </Box>
  );
}
